<template>
  <div ref="giftCardFormContainer">
    <div class="page-header bordered">
      <h1>
        Gift Cards

        <small class="float-right">
          <b-button v-b-modal.giftCardPurchasesModal class="btn btn-sm btn-primary">
            Purchases
          </b-button>
        </small>
      </h1>
    </div>

    <div v-if="stripeAccount.status === 'pending'" class="text-center">
      <b-alert variant="warning" show>
        To create Gift Cards you need to connect your &nbsp;
        <router-link to="/users/stripe-account">
            <i class="icon fa fa-cc-stripe"></i> Stripe Account
        </router-link>
      </b-alert>
    </div>

    <div v-else>
      <form>
        <ShowErrors
          :errorMessages="errorMessages"
        />

        <div class="form-group">
          <b-form-checkbox switch size="lg" v-model="giftCard.enabled"
            vid="enabled">
            Enable Gift Cards
          </b-form-checkbox>
        </div>

        <div v-show="giftCard.enabled">
          <h3 class="subheadline">Gift Card Details</h3>
          <div class="form-group">
            <label for="description">Description</label>
            <div v-show="markupDescriptionPreview" v-html="markedText(giftCard.description || '')" />
            <textarea class="form-control form-control-lg" name="description"
              v-model.trim="giftCard.description" data-vv-as="Description" rows="8"
              v-validate="'max:100000'" v-show="!markupDescriptionPreview" />
            <span v-show="errors.has('description')" class="input-error">
              {{ errors.first('description') }}
            </span>

            <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
              @click="markupDescriptionPreview = !markupDescriptionPreview">
              <i :class="['icon fa', {'fa-eye': !markupDescriptionPreview}, {'fa-edit': markupDescriptionPreview}]"></i>
              {{markupDescriptionPreview ? 'Edit Mode' : 'Preview'}}
            </button>
          </div>

          <div class="form-group">
            <label for="purchase_message" class="required">Next Steps After Purchase</label>
            <div v-show="markupPurchaseMessagePreview" v-html="markedText(giftCard.purchase_message || '')" />
            <textarea class="form-control form-control-lg" name="purchase_message"
              v-model.trim="giftCard.purchase_message" v-validate="'required|max:5000'"
              rows="8" data-vv-as="Next Steps After Purchase" v-show="!markupPurchaseMessagePreview" />
            <span v-show="errors.has('purchase_message')" class="input-error">
              {{ errors.first('purchase_message') }}
            </span>

            <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
              @click="markupPurchaseMessagePreview = !markupPurchaseMessagePreview">
              <i :class="['icon fa', {'fa-eye': !markupPurchaseMessagePreview}, {'fa-edit': markupPurchaseMessagePreview}]"></i>
              {{markupPurchaseMessagePreview ? 'Edit Mode' : 'Preview'}}
            </button>
          </div>

          <div class="form-group">
            <b-form-checkbox switch size="lg" v-model="giftCard.limited_time_bonus_enabled"
              vid="enabled">
              Limited-time bonus
            </b-form-checkbox>
          </div>

          <div v-show="giftCard.limited_time_bonus_enabled">
            <div class="form-group">
              <div class="row">
                <div class="col-lg-4">
                  <label for="limited_time_bonus_date" class="required">
                    For gift card purchases until
                  </label>
                  <input type="text" name="limited_time_bonus_date"
                    class="form-control form-control-lg inputDate" data-vv-as="Bonus Date"
                    :class="[{ 'is-invalid': errors.has('limited_time_bonus_date') }]"
                    v-model="giftCard.limited_time_bonus_date"
                    v-validate="{ required: giftCard.enabled && giftCard.limited_time_bonus_enabled }" />
                  <span v-show="errors.has('limited_time_bonus_date')" class="input-error">
                    {{ errors.first('limited_time_bonus_date') }}
                  </span>
                </div>

                <div class="col-lg-4">
                  <div class="form-group">
                    <label for="bonus_time_zone" class="required">Bonus time zone</label>
                    <select name="bonus_time_zone" class="form-control form-control-lg ui-select"
                      v-model="giftCard.bonus_time_zone" data-vv-as="Time zone"
                      v-validate="{ required: giftCard.enabled && giftCard.limited_time_bonus_enabled }"
                      >
                      <option value="null" disabled> Select time zone... </option>
                      <option v-for="(timeZone, index) in timeZoneList" :key="index"
                        :value="timeZone.value">
                        {{ timeZone.name }}
                      </option>
                    </select>
                    <span v-show="errors.has('bonus_time_zone')" class="input-error">
                      {{ errors.first('bonus_time_zone') }}
                    </span>
                  </div>
                </div>

                <div class="col-lg-3">
                  <label for="'bonus_percentage'" class="required">
                    Percent Bonus
                  </label>
                  <b-input-group append="%" class="mb-2 mr-sm-2 mb-sm-0">
                    <input type="number" class="form-control form-control-lg"
                      name="bonus_percentage" v-model="giftCard.bonus_percentage"
                      v-validate="{ required: giftCard.enabled && giftCard.limited_time_bonus_enabled }"
                      pattern="[1-9]" data-vv-as="Percent Bonus"
                      min="0" />
                  </b-input-group>

                  <span v-show="errors.has('bonus_percentage')" class="input-error">
                    {{ errors.first('bonus_percentage') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <h3 class="subheadline">
            Gift Card Image <small> (Maximum upload image size: 8MB) </small>
          </h3>
          <div class="form-group">
            <div class="image-preview" v-if="imageData.length > 0 || (giftCard.image_url && giftCard.image_url.length > 0)">
              <img class="preview" :src="imageData || giftCard.image_url">
              <br/>
              <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
                @click="removeImage">
                <i class="icon fa fa-trash"></i> Remove Image
              </button>
              &nbsp;
              <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
                @click="changeImage">
                <i class="icon fa fa-trash"></i> Change Image
              </button>
            </div>
            <div v-else class="ui-dropzone" @click="changeImage">
              <div class="icon"></div>
              <div>Click to upload an image</div>
            </div>
            <input type="file" class="form-control form-control-lg" ref="giftCardImage"
              @change="previewImage" accept="image/png, image/jpeg" style="display: none;"
              data-max-size="8192" data-vv-as="Image" name="image" id="gift-card-image"
              v-validate="'image|ext:png,jpg|size:8192'">

            <span v-show="errors.has('image')" class="input-error">
              {{ errors.first('image') }}
            </span>
          </div>

          <br/>
          <hr/>
        </div>

        <div class="form-group">
          <button type="button" class="btn btn-lg btn-primary" @click="validate()">
            Save Gift Card
          </button>
        </div>
      </form>

      <GiftCardPurchasesModal
        :giftCardId="giftCard.id">
      </GiftCardPurchasesModal>
    </div>
  </div>
</template>

<script>
import GiftCardService from '../../../services/GiftCardService'
import ApplicationSettingService from '../../../services/ApplicationSettingService'
import StripeAccountService from '../../../services/StripeAccountService'
import OrganizerProfileService from '../../../services/OrganizerProfileService'
import ShowErrors from '../../common/ShowErrors'
import GiftCardPurchasesModal from './PurchasesModal'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import axios from 'axios'

const objectToFormData = require('object-to-formdata')
const defaultValuePurchaseMessage = `**How to redeem**

If you specified your recipient's email address, they will receive an email from us with their gift card code.
If you did not, we will email the gift card code to you, which you can then forward to your recipient. Anyone who has the code can use it.
To redeem, please choose one of our show dates, and enter the gift card code from the email when prompted during checkout.
The value on the gift card will never expire, so come see us whenever it's convenient..
`
const defaultValueDescription = 'Give a unique gift to your friends and family! ' +
'Good for organizer_name online purchases. Not redeemable for on-site purchases, such as drinks. ' +
'Remaining gift card value never expires.'

export default {
  name: 'GiftCardForm',
  components: {
    ShowErrors,
    GiftCardPurchasesModal
  },
  data: function () {
    return {
      timeZoneList: this.$timeZoneList,
      stripeAccount: {},
      organizerProfile: {},
      markupPurchaseMessagePreview: false,
      markupDescriptionPreview: false,
      applicationSettings: {
        TICKET_FEE: {
          value: 0
        }
      },
      errorMessages: [],
      imageData: '',
      giftCard: {
        id: null,
        description: defaultValueDescription,
        purchase_message: defaultValuePurchaseMessage,
        image: null,
        enabled: true,
        bonus_percentage: 1,
        limited_time_bonus_date: null,
        limited_time_bonus_enabled: false,
        bonus_time_zone: null
      }
    }
  },
  watch: {
    '$route' (to, from) {
      this.clearForm()
      this.removeImage()
      this.errors.clear()
    }
  },
  created () {
    this.getOrganizerProfile()
    this.getStripeAccountInfo()
    this.getApplicationSettings()
    this.getGiftCard()
  },
  mounted () {
    let inputDate = flatpickr('.inputDate', {
      enableTime: true,
      defaultDate: this.giftCard.limited_time_bonus_date,
      dateFormat: 'Y-m-d H:i'
    })
    inputDate.input.removeAttribute('readOnly')
  },
  methods: {
    getGiftCard () {
      var self = this
      var loader = this.$loading.show()

      const giftCardRequest = GiftCardService.getGiftCard()
      const organizerProfileRequest = OrganizerProfileService.getOrganizerProfile()

      axios.all([giftCardRequest, organizerProfileRequest])
        .then(axios.spread((giftCardResponse, organizerProfileResponse) => {
          if (giftCardResponse.data) {
            self.giftCard = giftCardResponse.data
          }

          self.organizerProfile = organizerProfileResponse.data

          if (!self.giftCard.bonus_time_zone) {
            self.giftCard['bonus_time_zone'] = self.organizerProfile.time_zone
          }
        })).finally(() => {
          loader.hide()
        })
    },
    getOrganizerProfile () {
      var self = this

      OrganizerProfileService.getOrganizerProfile().then((response) => {
        self.organizerProfile = response.data

        if (self.giftCard.description === defaultValueDescription) {
          if (self.organizerProfile.organizer_name && self.organizerProfile.organizer_name.length > 0) {
            self.giftCard.description = defaultValueDescription.replace('organizer_name', self.organizerProfile.organizer_name)
          } else {
            self.giftCard.description = defaultValueDescription.replace('organizer_name', this.$localStorage.get('user_full_name'))
          }
        }
      })
    },
    getStripeAccountInfo () {
      var self = this
      var stripeAccountReference = this.$localStorage.get('user_stripe_acocunt_reference')

      StripeAccountService.getStripeAccount(stripeAccountReference).then((response) => {
        self.stripeAccount = response.data
      })
    },
    getApplicationSettings () {
      var self = this

      ApplicationSettingService.getApplicationSetting().then((response) => {
        response.data.forEach(function (appSetting, index) {
          self.applicationSettings[appSetting.name] = {
            value: appSetting.value,
            id: appSetting.id
          }
        })
      })
    },
    removeImage () {
      this.imageData = ''
      this.giftCard['image_url'] = ''
      this.giftCard['remove_image'] = true
    },
    changeImage () {
      this.$refs.giftCardImage.click()
    },
    previewImage: function (event) {
      // Reference to the DOM input element
      var input = event.target

      // Ensure that you have a file before attempting to read it
      if (input.files && input.files[0]) {
        var reader = new FileReader()
        reader.onload = (e) => {
          this.imageData = e.target.result
        }
        reader.readAsDataURL(input.files[0])

        this.giftCard['image'] = input.files[0]
        this.giftCard['remove_image'] = false
      }
    },
    validate () {
      var self = this

      this.$validator.validateAll().then((result) => {
        if (result) {
          self.save()
        } else {
          self.$swal({
            title: 'Gift Card invalid!',
            text: 'Please check form errors',
            type: 'warning'
          })
        }
      })
    },
    save () {
      this.errorMessages = []
      var self = this
      var apiRequest = null
      var loader = this.$loading.show({
        container: this.$refs.giftCardFormContainer
      })

      // Remove image key if empty to build FormData object
      if (!this.giftCard['image']) {
        delete this.giftCard['image']
      }

      const formData = objectToFormData(this.giftCard, null, null, 'gift_card')

      if (isNaN(this.giftCard.id) || this.giftCard.id === null) {
        apiRequest = GiftCardService.createGiftCard(formData)
      } else {
        apiRequest = GiftCardService.updateGiftCard(this.giftCard.id, formData)
      }

      apiRequest.then((response) => {
        self.$swal({
          title: 'Gift card saved!',
          type: 'success'
        })
      }).catch((error) => {
        if (error) {
          self.$swal({
            title: 'Error saving gift card!',
            type: 'error'
          })

          self.errorMessages = error.response.data.message || [error.response.data.error] || error.response.data.errors[0]
        }
      }).finally(() => {
        loader.hide()
      })
    },
    clearForm () {
      this.giftCard['id'] = ''
      this.giftCard['description'] = defaultValueDescription
      this.giftCard['purchase_message'] = defaultValuePurchaseMessage
      this.giftCard['image'] = ''
      this.giftCard['enabled'] = true
      this.giftCard['bonus_percentage'] = 1
      this.giftCard['limited_time_bonus_date'] = null
      this.giftCard['limited_time_bonus_enabled'] = false
    }
  }
}
</script>
